import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import { FaInstagram } from 'react-icons/fa';
import RetroWordmark from './RetroWordmark';
import './Home.css';
import { API_URL, FOUNDER_EMAILS } from '../config';
import '../styles/layout.css';

// Colors matching SpiceRack.js exactly
const colors = [
  '#00FF9F', // UI Green
  '#FF1493', // Deep pink
  '#0066FF', // Bright blue
  '#FF6B00', // Orange
  '#FF00FF', // Hot pink
  '#FFB800', // Amber
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

// Colors matching ImageCard.js for showcaseColors
const showcaseColors = [
  '#00FF9F', // UI Green
  '#FF1493', // Deep pink
  '#0066FF', // Bright blue
  '#FF6B00', // Orange
  '#FF00FF', // Hot pink
  '#FFB800', // Amber
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

// Placeholder phrases for typewriter effect
const PLACEHOLDER_PHRASES = [
  "a retro sunset over neon city",
  "vintage arcade cabinet",
  "80s synthwave landscape",
  "retro robot drinking coffee",
  "neon palm trees on beach",
  "vintage car in space",
  "retro diner at midnight",
  "cyberpunk cat with sunglasses"
];

// Generate consistent random properties for each spice (matching SpiceRack.js exactly)
const getSpiceProperties = (spiceId) => {
  // Use spice ID as seed for consistent randomness
  const seed = spiceId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const colorIndex = seed % colors.length;
  const rotation = (seed % 21) - 10; // Random rotation between -10 and +10 degrees
  
  return {
    color: colors[colorIndex],
    rotation: rotation
  };
};

const ShowcaseImage = ({ image, index, selectedSpices }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Random animation properties for initial placement
  const [randomRotation] = useState(() => Math.random() * 8 - 4); // -4 to +4 degrees
  const [bgRandomRotation] = useState(() => Math.random() * 12 - 6); // -6 to +6 degrees
  const [hoverRotation, setHoverRotation] = useState(0);
  const [bgHoverRotation, setBgHoverRotation] = useState(0);
  const [hoverColor, setHoverColor] = useState(showcaseColors[0]);
  
  // Random animation properties for sporadic entrance
  const [entranceRotation] = useState(() => Math.random() * 30 - 15); // -15 to +15 degrees
  const [entranceDelay] = useState(() => Math.random() * 2); // Random delay 0-2 seconds
  const [entranceX] = useState(() => Math.random() * 20 - 10); // -10 to +10px
  const [entranceY] = useState(() => Math.random() * 20 - 10); // -10 to +10px
  
  const thumbnailUrl = image?.thumbnailUrl || image?.url;
  
  // Check if this image contains any of the selected spices
  const imageSpices = image.spiceIds || [];
  const hasSelectedSpice = selectedSpices.length > 0 && selectedSpices.some(spiceId => 
    imageSpices.includes(spiceId)
  );
  
  // When spices are selected: highlight matching images
  const shouldHighlight = selectedSpices.length > 0 && hasSelectedSpice;
  
  // Get all matching spices for potential multi-spice effects
  const matchingSpices = selectedSpices.filter(spiceId => imageSpices.includes(spiceId));
  const primarySpice = matchingSpices[0]; // Use first matching spice for color
  const spiceProps = primarySpice ? getSpiceProperties(primarySpice) : null;

  // Generate new random rotations when highlighted (like ImageCard hover)
  useEffect(() => {
    if (shouldHighlight) {
      setHoverRotation(Math.random() * 8 - 4); // Same as ImageCard
      setBgHoverRotation(Math.random() * 12 - 6); // Same as ImageCard
    }
  }, [shouldHighlight]);

  // Generate new random rotations on hover (like ImageCard)
  useEffect(() => {
    if (isHovered) {
      setHoverRotation(Math.random() * 8 - 4); // -4 to +4 degrees
      setBgHoverRotation(Math.random() * 12 - 6); // -6 to +6 degrees
      setHoverColor(showcaseColors[Math.floor(Math.random() * showcaseColors.length)]);
    }
  }, [isHovered]);

  // Determine if we should show background effects (either from hover or spice selection)
  const showBackgroundCard = isHovered || shouldHighlight;
  
  // Determine background color - prioritize spice color when available
  const bgColor = shouldHighlight ? (spiceProps?.color || '#00FF9F') : hoverColor;

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <motion.div
      className="showcase-grid-item"
      style={{ 
        overflow: 'visible',
        margin: '4px'
      }}
      initial={{ 
        opacity: 0,
        scale: 0.3,
        rotate: entranceRotation,
        x: entranceX,
        y: entranceY
      }}
      animate={{ 
        opacity: isLoaded ? 1 : 0,
        scale: isLoaded ? 1 : 0.3,
        rotate: isLoaded ? randomRotation : entranceRotation,
        x: isLoaded ? 0 : entranceX,
        y: isLoaded ? 0 : entranceY
      }}
      transition={{ 
        type: "spring",
        stiffness: 400,
        damping: 30,
        mass: 0.6,
        delay: entranceDelay
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background card - show when hovered or highlighted with appropriate color */}
      <AnimatePresence>
        {showBackgroundCard && (
          <motion.div
            className="absolute inset-0 rounded-lg shadow-lg"
            style={{
              background: bgColor,
              transformOrigin: 'center center',
              zIndex: 1
            }}
            initial={{ 
              rotate: bgRandomRotation,
              scale: 1.05, // Same as ImageCard
              opacity: 0
            }}
            animate={{ 
              rotate: bgHoverRotation, // Use the hover rotation
              scale: 1.08, // Same as ImageCard
              opacity: 0.8,
              x: Math.random() * 4 - 2, // Same random movement as ImageCard
              y: Math.random() * 4 - 2
            }}
            exit={{
              opacity: 0,
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            transition={{
              type: "spring", // Match ImageCard exactly
              stiffness: 300,
              damping: 25,
              mass: 1
            }}
          />
        )}
      </AnimatePresence>

      {/* Main image card */}
      <motion.div
        className="relative w-full h-full rounded-lg overflow-hidden bg-black/5"
        style={{ 
          zIndex: showBackgroundCard ? 10 + matchingSpices.length : 2,
          filter: 'none',
          backdropFilter: 'none'
        }}
        animate={{
          scale: showBackgroundCard ? 1.02 : 1, // Same as ImageCard hover
          rotate: showBackgroundCard ? hoverRotation : randomRotation, // Use hover rotation when highlighted
        }}
        transition={{
          type: "spring", // Match ImageCard exactly
          stiffness: 300,
          damping: 25,
          mass: 1
        }}
      >
        {thumbnailUrl && (
          <img 
            src={thumbnailUrl}
            alt=""
            className="w-full h-full object-cover"
            style={{ 
              opacity: 1,
              filter: 'none',
              backdropFilter: 'none'
            }}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

const SpicePill = ({ spice, isActive, onClick }) => {
  const spiceProps = getSpiceProperties(spice.id);
  
  return (
    <button
      onClick={() => onClick(spice.id)}
      className={`rounded-full px-3 py-2 flex items-center gap-1.5 border inline-flex ${
        isActive 
          ? 'border-transparent' 
          : 'border-gray-200 hover:border-gray-400 transition-all duration-200'
      }`}
      style={{
        backgroundColor: isActive ? spiceProps.color : '#F5F4F2',
        transform: isActive ? `rotate(${spiceProps.rotation * 0.5}deg)` : 'rotate(0deg)',
        transition: 'transform 0.15s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
      }}
    >
      <span 
        className="text-sm transition-all duration-200"
        style={{
          filter: isActive ? 'grayscale(0%) saturate(130%)' : 'grayscale(30%)',
          color: isActive 
            ? (spiceProps.color === '#00FF9F' ? 'rgba(0, 0, 0, 0.9)' : 'white')
            : 'rgba(0, 0, 0, 0.85)'
        }}
      >
        {spice.emoji}
      </span>
      <span
        className="font-['VT323'] text-sm whitespace-nowrap"
        style={{
          color: isActive 
            ? (spiceProps.color === '#00FF9F' ? 'rgba(0, 0, 0, 0.9)' : 'white')
            : 'rgba(0, 0, 0, 0.85)'
        }}
      >
        {spice.name}
      </span>
    </button>
  );
};

const Home = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [showcaseImages, setShowcaseImages] = useState([]);
  const [availableSpices, setAvailableSpices] = useState([]);
  const [selectedSpices, setSelectedSpices] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const textareaRef = useRef(null);
  const imagesLoadedRef = useRef(false);
  const retroWordmarkRef = useRef(null);
  
  // Typewriter effect states
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(() => Math.floor(Math.random() * PLACEHOLDER_PHRASES.length));
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [charIndex, setCharIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  // Pulse the RetroWordmark every 1 second (only on Home page)
  useEffect(() => {
    const pulseInterval = setInterval(() => {
      if (retroWordmarkRef.current) {
        // Trigger the click event to animate the wordmark
        retroWordmarkRef.current.click();
      }
    }, 1000);

    return () => clearInterval(pulseInterval);
  }, []);

  // Cursor blink effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => clearInterval(cursorInterval);
  }, []);

  // Typewriter effect
  useEffect(() => {
    const currentPlaceholder = PLACEHOLDER_PHRASES[currentPlaceholderIndex];
    
    const typewriterInterval = setInterval(() => {
      if (isTyping) {
        // Typing phase
        if (charIndex < currentPlaceholder.length) {
          setDisplayedText(currentPlaceholder.slice(0, charIndex + 1));
          setCharIndex(prev => prev + 1);
        } else {
          // Finished typing, pause then start backspacing
          setTimeout(() => {
            setIsTyping(false);
          }, 1500); // Pause for 1.5 seconds after typing
        }
      } else {
        // Backspacing phase
        if (charIndex > 0) {
          setCharIndex(prev => prev - 1);
          setDisplayedText(currentPlaceholder.slice(0, charIndex - 1));
        } else {
          // Finished backspacing, move to random placeholder
          setCurrentPlaceholderIndex(Math.floor(Math.random() * PLACEHOLDER_PHRASES.length));
          setIsTyping(true);
          setCharIndex(0);
          setDisplayedText('');
        }
      }
    }, isTyping ? 100 : 50); // Typing: 100ms per char, Backspacing: 50ms per char

    return () => clearInterval(typewriterInterval);
  }, [currentPlaceholderIndex, charIndex, isTyping]);

  // Create placeholder text with consistent cursor space (no jumping)
  const placeholderWithCursor = displayedText + (showCursor ? '|' : ' '); // Regular space has same width as | in monospace

  // Load spice configuration from server and showcase images, then select spices based on what's available
  useEffect(() => {
    const fetchSpiceConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/stylekit`);
        if (!response.ok) {
          console.warn('Failed to fetch spice config');
          return;
        }
        
        const config = await response.json();
        console.log('Fetched spice config:', config);
        
        // Store the full config for later use with dynamic selection
        window.styleKitConfig = config;
        
      } catch (error) {
        console.error('Error fetching spice config:', error);
      }
    };

    fetchSpiceConfig();
  }, []);

  // Function to dynamically select spices based on loaded images
  const selectDiverseSpices = (images, config) => {
    if (!config || !images.length) return [];

    // Get all unique spice IDs from the loaded images
    const allImageSpices = new Set();
    images.forEach(image => {
      if (image.spiceIds && Array.isArray(image.spiceIds)) {
        image.spiceIds.forEach(spiceId => allImageSpices.add(spiceId));
      }
    });

    const categories = ['descriptors', 'styles', 'vibes', 'time', 'lens', 'film'];
    const selectedSpices = [];

    // Try to get at least one spice from each category
    categories.forEach(categoryKey => {
      if (config[categoryKey]?.items && selectedSpices.length < 6) {
        const availableSpicesInCategory = config[categoryKey].items.filter(item => 
          allImageSpices.has(item.id)
        );
        
        if (availableSpicesInCategory.length > 0) {
          // Randomly pick one from this category
          const randomSpice = availableSpicesInCategory[
            Math.floor(Math.random() * availableSpicesInCategory.length)
          ];
          selectedSpices.push(randomSpice);
        }
      }
    });

    // If we need more spices to reach 6, randomly pick from remaining available spices
    if (selectedSpices.length < 6) {
      const usedIds = new Set(selectedSpices.map(s => s.id));
      const remainingSpices = [];
      
      categories.forEach(categoryKey => {
        if (config[categoryKey]?.items) {
          config[categoryKey].items.forEach(item => {
            if (allImageSpices.has(item.id) && !usedIds.has(item.id)) {
              remainingSpices.push(item);
            }
          });
        }
      });

      // Shuffle remaining and pick until we have 6
      const shuffled = remainingSpices.sort(() => Math.random() - 0.5);
      const needed = 6 - selectedSpices.length;
      selectedSpices.push(...shuffled.slice(0, needed));
    }

    return selectedSpices;
  };

  // Ensure spices are selected when both config and images are ready
  useEffect(() => {
    if (window.styleKitConfig && showcaseImages.length > 0 && availableSpices.length === 0) {
      const diverseSpices = selectDiverseSpices(showcaseImages, window.styleKitConfig);

      setAvailableSpices(diverseSpices);
    }
  }, [showcaseImages, availableSpices.length]);

  // Fetch all founder favorites for maximum showcase content
  useEffect(() => {
    const fetchAllFounderFavorites = async () => {
      try {
        const responses = await Promise.all(
          FOUNDER_EMAILS.map(async email => {
            try {
              const response = await fetch(`${API_URL}/api/favorites/public/founder?founder=${encodeURIComponent(email)}`);
              if (!response.ok) {

                return [];
              }
              const images = await response.json();

              return images;
            } catch (error) {

              return [];
            }
          })
        );

        const allImages = responses.flat();
        const validImages = allImages.filter(img => img && (img.url || img.s3Url));
        
        if (validImages.length > 0) {
          // Shuffle and take unique images (no duplicates)
          const shuffledImages = [...validImages].sort(() => Math.random() - 0.5);
          const uniqueImages = [];
          const seenUrls = new Set();
          
          for (const img of shuffledImages) {
            const imageUrl = img.url || img.s3Url;
            if (!seenUrls.has(imageUrl) && uniqueImages.length < 50) { // Increased to 50 for more variety
              seenUrls.add(imageUrl);
              uniqueImages.push(img);
            }
          }
          
          // Calculate how many images we need to fill the screen
          const calculateImagesNeeded = () => {
            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;
            
            // Estimate grid based on screen size (no padding since images span full screen)
            const minImageSize = screenWidth <= 480 ? 120 : screenWidth <= 768 ? 150 : 200;
            const gap = screenWidth <= 480 ? 4 : screenWidth <= 768 ? 8 : 16; // gap in pixels (reduced)
            
            const availableWidth = screenWidth;
            const availableHeight = screenHeight;
            
            const itemsPerRow = Math.floor((availableWidth + gap) / (minImageSize + gap));
            const rowsNeeded = Math.ceil((availableHeight + gap) / (minImageSize + gap));
            
            return Math.max(itemsPerRow * rowsNeeded, 20); // Increased minimum for full coverage
          };
          
          const imagesNeeded = calculateImagesNeeded();
          
          // Repeat images if we don't have enough to fill the screen
          let finalImages = [...uniqueImages];
          while (finalImages.length < imagesNeeded && uniqueImages.length > 0) {
            const imagesToAdd = uniqueImages.slice(0, Math.min(uniqueImages.length, imagesNeeded - finalImages.length));
            finalImages = [...finalImages, ...imagesToAdd.map(img => ({ ...img, id: `${img.id}-repeat-${Math.random()}` }))];
          }
          
          // Take exactly the number we need
          setShowcaseImages(finalImages.slice(0, imagesNeeded));
          imagesLoadedRef.current = true;
          
          // Now select diverse spices based on what's actually available in these images
          if (window.styleKitConfig) {
            const diverseSpices = selectDiverseSpices(finalImages, window.styleKitConfig);

            setAvailableSpices(diverseSpices);
          }
          
        }
      } catch (error) {
        console.error('Error fetching founder favorites:', error);
      }
    };

    fetchAllFounderFavorites();
    
    // Recalculate on window resize
    const handleResize = () => {
      if (imagesLoadedRef.current) {
        fetchAllFounderFavorites();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSpiceToggle = (spiceId) => {
    const spice = availableSpices.find(s => s.id === spiceId);
    if (!spice) return;
    
    // Toggle spice in selectedSpices array for image filtering
    setSelectedSpices(prev => {
      const isSelected = prev.includes(spiceId);
      return isSelected 
        ? prev.filter(id => id !== spiceId)
        : [...prev, spiceId];
    });
  };

  // Check if a spice is active by checking the selectedSpices array
  const isSpiceActive = (spice) => {
    return selectedSpices.includes(spice.id);
  };

  const handleStartCreating = () => {
    if (isAuthenticated) {
      // Navigate to create page with current prompt
      navigate('/create', { state: { prompt: currentPrompt, spices: selectedSpices } });
    } else {
      // Redirect to login
      loginWithRedirect({
        appState: { 
          returnTo: '/create',
          prompt: currentPrompt,
          spices: selectedSpices
        }
      });
    }
  };

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [currentPrompt]);

  const fullPrompt = currentPrompt;

  return (
    <div className="showcase-home-container">
      {/* Login Button - Responsive positioning */}
      <motion.button
        onClick={handleStartCreating}
        className="fixed z-20 px-4 py-2 rounded-lg font-['VT323'] text-lg font-normal transition-all duration-200 hover:scale-105 
                   top-4 right-4 md:top-4 md:right-4 
                   sm:top-2 sm:right-2 sm:px-3 sm:py-1.5 sm:text-base"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
        style={{
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          color: 'rgba(0, 0, 0, 0.85)',
          backgroundColor: '#00FF9F80'
        }}
      >
        Login
      </motion.button>

      {/* Image Showcase Grid */}
      <div className="showcase-images-section">
        <motion.div 
          className="showcase-images-grid"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          {showcaseImages.map((image, index) => (
            <ShowcaseImage 
              key={image.id} 
              image={image} 
              index={index}
              selectedSpices={selectedSpices}
            />
          ))}
        </motion.div>
        
        {/* Overlay only affects background images, not floating content */}
        <div 
          className="absolute inset-0 bg-[rgba(244,243,240,0.75)] transition-opacity duration-300"
          style={{
            opacity: selectedSpices.length > 0 ? 0.4 : 0.75,
            pointerEvents: 'none'
          }}
        />
      </div>

      {/* Content Container - Responsive design */}
      <div className="compact-content-container md:block hidden">
        <motion.div 
          className="compact-content-card"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ 
            duration: 0.8,
            delay: 0.2,
            ease: "easeOut"
          }}
        >
          {/* Centered Logo */}
          <motion.div 
            className="compact-logo"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
          >
            <RetroWordmark ref={retroWordmarkRef} size="2xl" />
          </motion.div>

          {/* Compact Tagline */}
          <motion.h2 
            className="compact-tagline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          >
            Blend brands. Create magic.
          </motion.h2>

          {/* Prompt Input */}
          <motion.div 
            className="compact-prompt-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
          >
            <textarea
              ref={textareaRef}
              value={currentPrompt}
              onChange={(e) => setCurrentPrompt(e.target.value)}
              placeholder={placeholderWithCursor}
              className="compact-prompt-input"
              style={{ minHeight: '60px' }}
            />
          </motion.div>

          {/* Interactive Spice Selection - All Available Spices */}
          <motion.div 
            className="compact-spices-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
          >
            <h4 className="compact-spices-title">Add Blatant Spice</h4>
            <div className="compact-spices-grid">
              {availableSpices.map((spice) => (
                <SpicePill
                  key={spice.id}
                  spice={spice}
                  isActive={isSpiceActive(spice)}
                  onClick={handleSpiceToggle}
                />
              ))}
            </div>
          </motion.div>

          {/* CTA Button - Match ImageCreator.js styling exactly */}
          <motion.button
            onClick={handleStartCreating}
            className="px-6 py-2 rounded-xl transition-all w-full font-['VT323'] text-lg btn btn-green"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
            whileHover={{ 
              scale: 1.02
            }}
            whileTap={{ scale: 0.98 }}
          >
            Start Creating
          </motion.button>

          {/* Subtext */}
          <motion.p 
            className="compact-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
          >
            Free to start • No credit card required
          </motion.p>
        </motion.div>

        {/* Bottom hint */}
        <motion.div 
          className="showcase-hint"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease: "easeOut" }}
        >
          <a 
            href="https://www.instagram.com/blatant.space" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <span>brought to you by Blatant Space</span>
            <FaInstagram className="text-base" />
          </a>
        </motion.div>
      </div>

      {/* Mobile Full-Screen Content */}
      <div className="md:hidden fixed inset-0 flex flex-col h-screen w-full overflow-hidden z-50">
        {/* Mobile content container */}
        <motion.div 
          className="flex flex-col h-full justify-center items-center px-4 py-8 bg-[rgba(244,243,240,0.92)] relative z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ 
            duration: 0.8,
            delay: 0.2,
            ease: "easeOut"
          }}
        >
          {/* Mobile Logo */}
          <motion.div 
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
          >
            <RetroWordmark ref={retroWordmarkRef} size="xl" />
          </motion.div>

          {/* Mobile Tagline */}
          <motion.h2 
            className="text-xl font-['VT323'] text-center text-gray-800 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          >
            Blend brands. Create magic.
          </motion.h2>

          {/* Mobile Prompt Input */}
          <motion.div 
            className="w-full mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
          >
            <textarea
              ref={textareaRef}
              value={currentPrompt}
              onChange={(e) => setCurrentPrompt(e.target.value)}
              placeholder={placeholderWithCursor}
              className="w-full p-4 rounded-lg border border-gray-300 font-['VT323'] text-lg resize-none 
                         focus:outline-none focus:ring-2 focus:ring-[#00FF9F] focus:border-transparent
                         bg-white/90 backdrop-blur-sm"
              style={{ minHeight: '60px', maxHeight: '120px' }}
            />
          </motion.div>

          {/* Mobile Spice Selection */}
          <motion.div 
            className="w-full mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
          >
            <h4 className="font-['VT323'] text-lg text-gray-800 mb-3 text-center">Add Blatant Spice</h4>
            <div className="flex flex-wrap gap-2 justify-center">
              {availableSpices.map((spice) => (
                <SpicePill
                  key={spice.id}
                  spice={spice}
                  isActive={isSpiceActive(spice)}
                  onClick={handleSpiceToggle}
                />
              ))}
            </div>
          </motion.div>

          {/* Mobile CTA Button */}
          <motion.button
            onClick={handleStartCreating}
            className="w-full px-6 py-4 rounded-xl transition-all font-['VT323'] text-xl btn btn-green"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
            whileHover={{ 
              scale: 1.02
            }}
            whileTap={{ scale: 0.98 }}
          >
            Start Creating
          </motion.button>

          {/* Mobile Subtext */}
          <motion.p 
            className="text-sm text-gray-600 text-center mt-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
          >
            Free to start • No credit card required
          </motion.p>

          {/* Mobile Bottom hint */}
          <motion.div 
            className="mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7, ease: "easeOut" }}
          >
            <a 
              href="https://www.instagram.com/blatant.space" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity text-sm text-gray-600"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <span>brought to you by Blatant Space</span>
              <FaInstagram className="text-base" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Home; 